import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { KeyRound, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const nav = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // {email}
  const [err, setErr] = useState(null);

  // Client-side sanity. Real validation happens server-side.
  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && password !== confirm;
  const canSubmit = token && password.length >= 8 && password === confirm && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setErr(null); setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        token,
        new_password: password,
      });
      setDone({ email: data?.email });
      // Redirect to login after a brief success pause.
      setTimeout(() => nav("/login", { replace: true, state: { resetEmail: data?.email } }), 1600);
    } catch (e2) {
      setErr(e2.userMessage || "Reset failed. The link may have expired or already been used.");
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center bg-background p-6">
        <div className="w-full max-w-md card-base p-6 text-center" data-testid="reset-no-token">
          <div className="w-10 h-10 mx-auto rounded-md bg-[#EF4444]/10 grid place-items-center border border-[#EF4444]/30">
            <KeyRound className="w-5 h-5 text-[#EF4444]" />
          </div>
          <h1 className="font-heading text-xl font-semibold mt-3">Missing reset token</h1>
          <p className="text-sm text-muted-foreground mt-1.5">
            This link is incomplete. Request a new password reset to continue.
          </p>
          <Link
            to="/forgot-password"
            className="mt-4 inline-flex items-center justify-center px-3 py-2 rounded-md bg-foreground text-background text-sm font-medium"
            data-testid="reset-no-token-cta"
          >
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-background p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-9 h-9 rounded-md bg-foreground text-background grid place-items-center font-heading font-bold">D</div>
          <div>
            <div className="font-heading font-semibold leading-tight">Dreamhouse</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Flow</div>
          </div>
        </div>

        <h1 className="font-heading text-3xl font-semibold tracking-tight" data-testid="reset-title">
          Choose a new password.
        </h1>
        <p className="text-muted-foreground mt-2 text-sm">
          At least 8 characters. This link can only be used once.
        </p>

        {done ? (
          <div className="mt-8 p-4 rounded-md border border-[#10B981]/30 bg-[#10B981]/5 flex items-start gap-3" data-testid="reset-success">
            <CheckCircle2 className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" />
            <div className="text-sm leading-relaxed">
              <div className="font-medium text-foreground">Password updated.</div>
              <div className="text-muted-foreground mt-1">
                Redirecting you to sign in{done.email ? ` as ${done.email}` : ""}…
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
            <div>
              <label className="label-tiny block mb-1.5">New password</label>
              <input
                type="password"
                value={password}
                onChange={(ev) => setPassword(ev.target.value)}
                required
                minLength={8}
                data-testid="reset-password"
                autoFocus
                placeholder="At least 8 characters"
                className="w-full px-3 py-2.5 rounded-md bg-zinc-950 border border-border focus:border-zinc-500 outline-none text-sm"
              />
              {tooShort && (
                <div className="text-[11px] text-[#F59E0B] mt-1" data-testid="reset-too-short">
                  Needs at least 8 characters.
                </div>
              )}
            </div>
            <div>
              <label className="label-tiny block mb-1.5">Confirm new password</label>
              <input
                type="password"
                value={confirm}
                onChange={(ev) => setConfirm(ev.target.value)}
                required
                minLength={8}
                data-testid="reset-confirm"
                className="w-full px-3 py-2.5 rounded-md bg-zinc-950 border border-border focus:border-zinc-500 outline-none text-sm"
              />
              {mismatch && (
                <div className="text-[11px] text-[#EF4444] mt-1" data-testid="reset-mismatch">
                  Passwords don&apos;t match.
                </div>
              )}
            </div>
            {err && (
              <div className="text-xs text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-md p-2.5" data-testid="reset-error">
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="reset-submit"
              className="w-full py-2.5 rounded-md bg-foreground text-background font-medium text-sm hover:bg-zinc-200 disabled:opacity-50"
            >
              {busy ? "Updating…" : "Update password"}
            </button>
            <p className="text-xs text-muted-foreground text-center pt-2">
              <Link to="/login" className="text-foreground underline underline-offset-2" data-testid="reset-back-login">
                Back to sign in
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
